"use client";

import React, {ReactElement} from "react";
import {StyledBoxColumn, StyledContainer} from "../../shared/StyledComponents";
import {Button, Card, Grid} from "@mui/material";
import {PD_LG, PD_XSM, SZ_MD, SZ_XLG} from "../../shared/dimens";
import {AddOutlined} from "@mui/icons-material";
import {FormGenContainerMode} from "../../shared/FormGenContainer";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE, DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN} from "../../shared/BaseApp";
import {Action} from "../../shared/types";
import {StoredDataFileUploadFragment} from "../../shared/StoredDateFileUploadFragment";
import {ActionsDialogContent} from "../../shared/Dialogs";
import {StyledCamera} from "../../shared/StyledCamera";
import {DesktopFile} from "../../shared/files";
import {MimeTypeMap} from "../../shared/mime_types";

export type GalleryUploadFile = {
  url: string,
  thumbnailUrl?: string,
  mimeType?: string,
}

export type GalleryUploadChange = {
  index: number,
  file: DesktopFile,
}

export class GalleryUpload extends React.Component<{
  readonly?: boolean,
  hideUpload?: boolean,
  galleryId: string,
  value: GalleryUploadFile[],
  mimeTypes?: string[],
  storedDataSourceBasePath: string,
  setValue: (value: GalleryUploadFile[], change: GalleryUploadChange) => void,
  showGalleryDialog: (urls: string[], selectedIndex?: number) => void,
}> {
  private onChangeFile(value: string, setValue: (url: string, file?: DesktopFile) => void) {
    BaseApp.CONTEXT.showDialog(null, () => this.showChangeFileOptions(value, setValue))
  }

  private showChangeFileOptions(value: string, setValue: (url: string, file?: DesktopFile) => void) {
    const actions: Action[] = [];
    if (!value) {
      actions.push(...[
        new Action("Take Photo", () => {
          const id = BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN}, () =>
            <StyledCamera
              onDone={image => BaseApp.CONTEXT.hideDialog(id)}
              autoUpload={true}
              onUploadDone={(urls: string[]) => {
                setValue(urls[0]);
              }}
              imageOptions={{keepAspectRatio: 4 / 3}}
            />);
        }),
        new Action("Upload", () => {
          BaseApp.CONTEXT.showDialog(null, () => <StoredDataFileUploadFragment
            isDialog={true}
            mimeTypes={this.props.mimeTypes}
            storedDataSource={`${this.props.storedDataSourceBasePath}/${this.props.galleryId}`}
            onSelectionChanged={(urls: string[], files?: DesktopFile[]) => {
              setValue(urls[0], files?.[0]);
            }}/>);
        }),
      ]);
    }
    if (value) {
      actions.push(...[
        new Action("View", () => {
          this.props.showGalleryDialog([value]);
        }),
        new Action("Remove", () => {
          setValue(null);
        }),
        new Action("Copy URL", () => {
          navigator.clipboard.writeText(value);
          BaseApp.CONTEXT.showToast("Copied.");
        }),
      ]);
    }
    return <ActionsDialogContent actions={actions}/>;
  }

  private toUploadFile(url: string, file: DesktopFile): GalleryUploadFile {
    const name = file?.file?.name || "File";
    const n = name?.lastIndexOf(".");
    const mimeType = file?.mimeType || MimeTypeMap.getInstance().getMimeTypeFromExtension(n >= 0 ? name.substring(n + 1) : null);
    return {url, mimeType: mimeType};
  }

  private renderThumbnail(file: GalleryUploadFile): ReactElement {
    if (MimeTypeMap.isImage(file.mimeType)) {
      return <img src={file.url} style={{width: "100%", height: "100%", objectFit: "cover"}}/>;
    }
    const IconType = MimeTypeMap.getIconForMimeType(file.mimeType);
    const color = MimeTypeMap.getColorForMimeType(file.mimeType);
    return <IconType style={{color: color, width: SZ_XLG, height: SZ_XLG}}/>;
  }

  render(): ReactElement {
    const uploadFiles = [...(this.props.value || [])];
    const allFiles = [...uploadFiles];
    if (!this.props.readonly && !this.props.hideUpload) {
      allFiles.push({url: "_upload"});
    }
    return <StyledContainer size={"xlg"} style={{padding: PD_XSM}}>
      <Grid container>
        {allFiles.map((file, index) => {
          const url = file.url;
          return <Grid xs={6} sm={4}>
            <StyledBoxColumn style={{width: "100%", alignItems: "center", padding: PD_LG}}>
              <Card style={{width: '100%', height: '100%', aspectRatio: 1}}>
                <Button
                  onClick={() =>
                    url !== "_upload"
                      ? this.onChangeFile(url, (url, file: DesktopFile) => {
                        uploadFiles[index] = this.toUploadFile(url, file);
                        this.props.setValue(uploadFiles, {index: index, file: file});
                      })
                      : this.onChangeFile(null, (url, file: DesktopFile) => {
                        uploadFiles.push(this.toUploadFile(url, file));
                        this.props.setValue(uploadFiles, {index: uploadFiles.length - 1, file: file});
                      })}
                  style={{
                    padding: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                  {url !== "_upload"
                    ? this.renderThumbnail(file)
                    : <AddOutlined style={{width: SZ_MD, height: SZ_MD}}/>}
                </Button>
              </Card>
            </StyledBoxColumn>
          </Grid>;
        })}
      </Grid>
    </StyledContainer>;
  }
}


export function renderImageGalleryUpload(galleryId: string, storedDataSourceBasePath: string, value: string[], setValue: (value: string[], change?: GalleryUploadChange) => void, showGalleryDialog: (urls: string[], selectedIndex?: number) => void, mode?: FormGenContainerMode) {
  switch (mode) {
    case FormGenContainerMode.READ:
      return null;
    default:
    case FormGenContainerMode.CREATE:
    case FormGenContainerMode.EDIT:
      return <GalleryUpload
        galleryId={galleryId}
        mimeTypes={["image/png", "image/jpeg", "image/jpg"]}
        storedDataSourceBasePath={storedDataSourceBasePath}
        value={value?.map(value => ({url: value, mimeType: MimeTypeMap.MimeType_image_jpeg}))}
        setValue={(value: GalleryUploadFile[], change: GalleryUploadChange) => setValue(value?.map(value => value.url), change)}
        showGalleryDialog={showGalleryDialog}
      />;
  }
}
